import type { ApiResponse, EntityMedia, EntityMediaType, ProviderDetail } from "@kuroo/contracts";
import { KurooApiError } from "./errors";

export type SdkMediaContext = {
  baseUrl: string;
  fetchImpl: typeof fetch;
  credentials: RequestCredentials;
  organizationHeaders: (input?: HeadersInit) => Headers;
  request: <T>(path: string, init?: RequestInit) => Promise<T>;
};

export async function createAdminImport(context: SdkMediaContext, file: File): Promise<{ id: string; fileId: string; status: string }> {
  const form = new FormData();
  form.set("file", file);
  const response = await context.fetchImpl(`${context.baseUrl}/api/v1/admin/imports`, { method: "POST", credentials: context.credentials, headers: context.organizationHeaders(), body: form });
  const payload = (await response.json()) as ApiResponse<{ id: string; fileId: string; status: string }>;
  if (!response.ok || "error" in payload) {
    const error = "error" in payload ? payload.error : { code: "IMPORT_FAILED", message: "No se pudo crear la importación." };
    throw new KurooApiError(error.code, error.message, error.requestId, error.details);
  }
  return payload.data;
}

export async function uploadFile(context: SdkMediaContext, file: File): Promise<{ id: string; key: string; sizeBytes: number; contentType: string }> {
  const form = new FormData();
  form.set("file", file);
  const response = await context.fetchImpl(`${context.baseUrl}/api/v1/files`, { method: "POST", credentials: context.credentials, headers: context.organizationHeaders(), body: form });
  const payload = (await response.json()) as ApiResponse<{ id: string; key: string; sizeBytes: number; contentType: string }>;
  if (!response.ok || "error" in payload) throw new KurooApiError("UPLOAD_FAILED", "No se pudo subir el archivo.");
  return payload.data;
}

async function uploadProviderPhotoAt(context: SdkMediaContext, path: string, file: File, fallbackMessage: string): Promise<ProviderDetail> {
  const form = new FormData();
  form.set("file", file);
  const response = await context.fetchImpl(`${context.baseUrl}${path}`, {
    method: "POST",
    credentials: context.credentials,
    headers: context.organizationHeaders(),
    body: form
  });
  let payload: ApiResponse<ProviderDetail>;
  try {
    payload = (await response.json()) as ApiResponse<ProviderDetail>;
  } catch {
    throw new KurooApiError("INVALID_API_RESPONSE", "La API devolvió una respuesta no válida.", response.headers.get("x-request-id") ?? undefined);
  }
  if (!response.ok || "error" in payload) {
    const error = "error" in payload ? payload.error : { code: "UPLOAD_FAILED", message: fallbackMessage };
    throw new KurooApiError(error.code, error.message, error.requestId, error.details);
  }
  return payload.data;
}

export function uploadProviderPhoto(context: SdkMediaContext, providerId: string, file: File): Promise<ProviderDetail> {
  return uploadProviderPhotoAt(context, `/api/v1/admin/providers/${encodeURIComponent(providerId)}/photo`, file, "No se pudo guardar la foto del proveedor.");
}

export function uploadMyProviderPhoto(context: SdkMediaContext, file: File): Promise<ProviderDetail> {
  return uploadProviderPhotoAt(context, "/api/v1/providers/me/photo", file, "No se pudo guardar la foto.");
}

export function listEntityMedia(context: SdkMediaContext, options: { entityType?: EntityMediaType; entityId?: string; status?: "active" | "inactive"; includeInactive?: boolean } = {}): Promise<EntityMedia[]> {
  const query = new URLSearchParams();
  if (options.entityType) query.set("entityType", options.entityType);
  if (options.entityId) query.set("entityId", options.entityId);
  if (options.status) query.set("status", options.status);
  if (options.includeInactive) query.set("includeInactive", "true");
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return context.request<EntityMedia[]>(`/api/v1/admin/media${suffix}`);
}

export async function createEntityMedia(context: SdkMediaContext, input: { entityType: EntityMediaType; entityId: string; file?: File; sourceUrl?: string; position?: number; altText?: string }): Promise<EntityMedia> {
  const form = new FormData();
  form.set("entityType", input.entityType);
  form.set("entityId", input.entityId);
  if (input.file) form.set("file", input.file);
  if (input.sourceUrl) form.set("sourceUrl", input.sourceUrl);
  if (input.position !== undefined) form.set("position", String(input.position));
  if (input.altText !== undefined) form.set("altText", input.altText);
  const response = await context.fetchImpl(`${context.baseUrl}/api/v1/admin/media`, { method: "POST", credentials: context.credentials, headers: context.organizationHeaders(), body: form });
  const payload = (await response.json()) as ApiResponse<EntityMedia>;
  if (!response.ok || "error" in payload) {
    const error = "error" in payload ? payload.error : { code: "MEDIA_CREATE_FAILED", message: "No se pudo crear el recurso multimedia." };
    throw new KurooApiError(error.code, error.message, error.requestId, error.details);
  }
  return payload.data;
}

export function updateEntityMedia(context: SdkMediaContext, mediaId: string, input: { position?: number; altText?: string | null; status?: "active" | "inactive" }): Promise<EntityMedia> {
  return context.request<EntityMedia>(`/api/v1/admin/media/${encodeURIComponent(mediaId)}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteEntityMedia(context: SdkMediaContext, mediaId: string): Promise<{ id: string; status: "inactive"; deleted: boolean }> {
  return context.request<{ id: string; status: "inactive"; deleted: boolean }>(`/api/v1/admin/media/${encodeURIComponent(mediaId)}`, { method: "DELETE" });
}
